// Copyright © 2019-2026 Focal Systems. All rights reserved.

/**
 * The Action Tool's field label table, mirrored for the card preview.
 *
 * A field's `label` in `info_tab_fields` / `top_content_fields` is not display text — it is an
 * i18n key the handheld resolves as `taskContent.normal.info.<label>`, falling back to the raw
 * value when there is no entry. So a field labelled `srq` prints "SRQ", and `msi` prints
 * "MSI Number" — or "MIN" for Morrisons, because many keys carry per-chain overrides.
 *
 * Generated from `libs/tasks/src/lib/i18n/en/tasks.json` in web-focal-os. English only: the card
 * preview shows what a store colleague sees, and these strings are the Action Tool's, not the back
 * office's, so they are deliberately not part of this app's own translations.
 */

/** `taskContent.normal.info` — base keys plus their `_<chain>` variants. */
const FIELD_LABELS: Record<string, string> = {
  aisle: "Aisle",
  boh: "BOH",
  boh_asda: "On Hand",
  boh_asda_test: "On Hand",
  boh_lidl_fr: "SOH",
  boh_lidl_gb: "SOH",
  boh_morrisons: "Stock Record",
  boh_waitrose: "SE",
  boh_woolworths: "SOH",
  casePack: "Case Pack",
  casePack_asda: "Case Size",
  casePack_asda_test: "Case Size",
  casePack_lidl_fr: "Case Size",
  casePack_lidl_gb: "Case Size",
  changeBy: "Change by",
  changeDate: "Change Date",
  changeTo: "Change To",
  commodity: "Commodity",
  complianceScore: "Compliance Score",
  complianceScore_asda: "Compliance",
  complianceScore_asda_test: "Compliance",
  daysOos: "Days OOS",
  daysOos_lidl_fr: "Days OFF SALE",
  daysOos_lidl_gb: "Days OFF SALE",
  daysOos_morrisons: "Days Gap",
  daysOos_waitrose: "Days OFF SALE",
  department: "Department",
  description: "Description",
  detected: "Detected",
  detectedCommodities: "Detected Commodities",
  detectedIssues: "Detected Issues",
  dsd: "DSD",
  duration: "Duration",
  empty: "?",
  expected: "Expected",
  firstSeen: "First detected",
  firstSeen_morrisons: "Image taken",
  hfss: "HFSS",
  hfssCompliant: "Compliant",
  hfssNotCompliant: "Not Compliant",
  hfssNotProvided: "Not provided",
  imageTaken: "Image taken",
  inventoryLevel: "Inventory Level",
  inventoryLevel_lidl_fr: "Stock On Hand Level",
  inventoryLevel_lidl_gb: "Stock On Hand Level",
  inventoryLevel_morrisons: "Stock Record Level",
  inventoryLevel_waitrose: "Stock Level",
  inventoryLocations: "Inventory Locations",
  inventoryLocations_lidl_fr: "Stock Locations",
  inventoryLocations_lidl_gb: "Stock Locations",
  inventoryLocations_morrisons: "Stock Locations",
  inventoryLocations_waitrose: "Stock Locations",
  issues: "Issues",
  item: "Item",
  itemNumber: "Item Number",
  itemsInQuestion: "Items in Question",
  lastReceived: "Last Received",
  locations: "Locations",
  mod: "MOD",
  mod_asda: "BAY",
  mod_asda_test: "BAY",
  mod_lidl_fr: "BAY",
  mod_lidl_gb: "BAY",
  mod_morrisons: "BAY",
  mod_waitrose: "BAY",
  mods: "Mods",
  mods_lidl_fr: "Bays/plans",
  mods_lidl_gb: "Bays/plans",
  mods_morrisons: "Bays/plans",
  mods_waitrose: "Bays",
  modularLocation: "Location",
  msi: "MSI Number",
  msi_asda: "Item Number",
  msi_asda_test: "Item Number",
  msi_lidl_fr: "Item Number",
  msi_lidl_gb: "Item Number",
  msi_morrisons: "MIN",
  msi_sainsburys: "SKU",
  msi_waitrose: "Line Number",
  msi_woolworths: "Article Number",
  na: "N/A",
  noInfoFound: "No Information found",
  numberOfMods: "Number of Mods",
  numberOfMods_lidl_fr: "Number of bays/plans",
  numberOfMods_lidl_gb: "Number of bays/plans",
  numberOfMods_morrisons: "Number of bays/plans",
  numberOfMods_waitrose: "Number of bays",
  numberOfProducts: "Number of Products",
  onHands: "On Hand",
  openImsButton: "Details",
  otherLocations: "Other locations",
  position: "Position",
  price: "Price",
  priceTagIssues: "Price Tag Issues",
  productName: "Product Name",
  quantityChange: "Quantity Change",
  recoupable: "Recoupable",
  restockThreshold: "Restock Threshold",
  rotationEndDate: "Rotation End Date",
  rotationStartDate: "Rotation Start Date",
  schedule: "Schedule",
  scheduleName: "Schedule Name",
  section: "Section",
  shelf: "Shelf",
  shelfFillRate: "Shelf Fill Rate",
  shelves: "Shelves",
  shelvesAffected: "Shelves Affected",
  side: "Side",
  srq: "SRQ",
  srqShort: "SRQ",
  srqShort_asda: "MAX SHELF",
  srqShort_asda_test: "MAX SHELF",
  srqShort_collins: "Allocation",
  srqShort_fairway: "Allocation",
  srqShort_food_parade: "Allocation",
  srqShort_glass_gardens: "Allocation",
  srqShort_lidl_fr: "SHC",
  srqShort_lidl_gb: "SHC",
  srqShort_woolworths: "Shelf Cap",
  srq_asda: "Shelf Capacity",
  srq_asda_test: "Shelf Capacity",
  srq_collins: "Allocation",
  srq_fairway: "Allocation",
  srq_food_parade: "Allocation",
  srq_glass_gardens: "Allocation",
  srq_lidl_fr: "SHC",
  srq_lidl_gb: "SHC",
  srq_woolworths: "Shelf Cap",
  timestamp: "Timestamp",
  upc: "UPC",
  upc_lidl_fr: "Product Code",
  upc_lidl_gb: "Product Code",
  upc_morrisons: "Barcode",
  valueChange: "Value Change",
  yes: "Yes",
};

/** `taskContent.details` — the value table behind the `localizedText` field type. */
const LOCALIZED_VALUES: Record<string, string> = {
  weekly: "Weekly",
};

/**
 * The text the Action Tool prints for a field's `label`.
 *
 * A chain-specific entry wins when the preview is scoped to that chain, mirroring i18next's
 * `_<context>` suffix lookup.
 */
export function resolveFieldLabel(label: string, chainName?: string): string {
  if (chainName) {
    const scoped = FIELD_LABELS[`${label}_${chainName}`];
    if (scoped) return scoped;
  }
  return FIELD_LABELS[label] ?? label;
}

/** The text a `localizedText` field prints for its resolved value. */
export function resolveLocalizedValue(value: string): string {
  return LOCALIZED_VALUES[value] ?? value;
}
